import { useEffect, useState } from 'react';
import { Activity, TrendingUp, AlertCircle, Calendar } from 'lucide-react';
import { config } from '../config';

interface AvailabilityStats {
  component_id: string;
  component_name: string;
  component_status: string;
  availability_percentage: number;
  uptime_percentage: number;
  total_incidents: number;
  total_maintenances: number;
  last_incident_date: string | null;
  last_maintenance_date: string | null;
}

interface AvailabilitySummary {
  period_days: number;
  start_date: string;
  end_date: string;
  total_components: number;
  operational_components: number;
  total_incidents: number;
  total_maintenances: number;
  avg_availability: number;
  avg_uptime: number;
  incident_breakdown: Array<{
    severity: string;
    count: number;
  }>;
}

interface DailyData {
  date: string;
  availability_percentage: number;
  status: string;
  incidents_count: number;
  maintenances_count: number;
}

export default function AvailabilityStats() {
  const [stats, setStats] = useState<AvailabilityStats[]>([]);
  const [summary, setSummary] = useState<AvailabilitySummary | null>(null);
  const [selectedComponent, setSelectedComponent] = useState<string | null>(null);
  const [dailyData, setDailyData] = useState<DailyData[]>([]);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);

  const apiUrl = config.apiUrl;

  function toNumber(value: string | number | undefined): number {
    if (value === undefined || value === null) return 0;
    return typeof value === 'string' ? parseFloat(value) : value;
  }

  useEffect(() => {
    fetchData();
  }, [days]);

  useEffect(() => {
    if (selectedComponent) {
      fetchDailyData(selectedComponent);
    }
  }, [selectedComponent, days]);

  async function fetchData() {
    try {
      setLoading(true);

      const statsResponse = await fetch(`${apiUrl}/v1/stats/availability/statistics?days=${days}`);
      if (statsResponse.ok) {
        const statsData = await statsResponse.json();
        setStats(statsData);
      }

      const summaryResponse = await fetch(`${apiUrl}/v1/stats/availability/summary?days=${days}`);
      if (summaryResponse.ok) {
        const summaryData = await summaryResponse.json();
        setSummary(summaryData);
      }
    } catch (error) {
      console.error('Failed to fetch availability data', error);
    } finally {
      setLoading(false);
    }
  }

  async function fetchDailyData(componentId: string) {
    try {
      const response = await fetch(`${apiUrl}/v1/stats/availability/component/${componentId}/daily?days=${days}`);
      if (response.ok) {
        const data = await response.json();
        setDailyData(data);
      }
    } catch (error) {
      console.error('Failed to fetch daily data', error);
    }
  }

  function getStatusColor(status: string): string {
    switch (status) {
      case 'OPERATIONAL': return 'bg-emerald-50 text-emerald-700';
      case 'DEGRADED_PERFORMANCE': return 'bg-yellow-50 text-yellow-700';
      case 'PARTIAL_OUTAGE': return 'bg-amber-50 text-amber-500';
      case 'MAJOR_OUTAGE': return 'bg-rose-50 text-rose-300';
      case 'MAINTENANCE': return 'bg-blue-50 text-blue-700';
      default: return 'bg-gray-50 text-gray-700';
    }
  }

  function getAvailabilityColor(percentage: number): string {
    if (percentage >= 99.9) return 'text-emerald-600';
    if (percentage >= 99.0) return 'text-yellow-600';
    if (percentage >= 95.0) return 'text-orange-600';
    return 'text-rose-300';
  }

  function formatDate(dateString: string | null): string {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="relative">
          <div className="w-10 h-10 border-2 border-gray-200 rounded-full" />
          <div className="absolute inset-0 w-10 h-10 border-2 border-transparent border-t-unlimit-lime rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="bg-navy-900 rounded-2xl p-8">
        <h1 className="text-2xl font-bold text-white flex items-center space-x-2.5">
          <Activity className="h-6 w-6 text-unlimit-lime" />
          <span>Availability Statistics</span>
        </h1>
        <p className="mt-1.5 text-gray-400 text-sm">
          Historical availability metrics and uptime statistics
        </p>
      </div>

      {/* Time Period Selector */}
      <div className="flex space-x-1 bg-white rounded-xl border border-gray-100 shadow-card p-1 w-fit">
        {[30, 60, 90].map((period) => (
          <button
            key={period}
            onClick={() => setDays(period)}
            className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${
              days === period
                ? 'bg-navy-900 text-white shadow-sm'
                : 'text-gray-500 hover:text-navy-900 hover:bg-gray-50'
            }`}
          >
            {period}d
          </button>
        ))}
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl shadow-card border border-gray-100 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Avg Availability</p>
                <p className={`text-2xl font-bold mt-1.5 tabular-nums ${getAvailabilityColor(summary.avg_availability)}`}>
                  {toNumber(summary.avg_availability).toFixed(2)}%
                </p>
              </div>
              <div className="w-10 h-10 bg-emerald-50 rounded-xl flex items-center justify-center">
                <TrendingUp className="h-5 w-5 text-emerald-600" />
              </div>
            </div>
            <p className="text-[11px] text-gray-400 mt-2">
              Uptime: {toNumber(summary.avg_uptime).toFixed(2)}%
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-card border border-gray-100 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Total Incidents</p>
                <p className="text-2xl font-bold text-navy-900 mt-1.5 tabular-nums">
                  {summary.total_incidents || 0}
                </p>
              </div>
              <div className="w-10 h-10 bg-rose-50 rounded-xl flex items-center justify-center">
                <AlertCircle className="h-5 w-5 text-rose-300" />
              </div>
            </div>
            <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
              {summary.incident_breakdown?.map((item) => (
                <span key={item.severity} className="text-gray-400">
                  {item.severity}: {item.count}
                </span>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-card border border-gray-100 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Maintenances</p>
                <p className="text-2xl font-bold text-navy-900 mt-1.5 tabular-nums">
                  {summary.total_maintenances || 0}
                </p>
              </div>
              <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center">
                <Calendar className="h-5 w-5 text-blue-500" />
              </div>
            </div>
            <p className="text-[11px] text-gray-400 mt-2">
              Scheduled maintenance windows
            </p>
          </div>

          <div className="bg-white rounded-xl shadow-card border border-gray-100 p-5">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs font-medium text-gray-400 uppercase tracking-wider">Components</p>
                <p className="text-2xl font-bold text-navy-900 mt-1.5 tabular-nums">
                  {summary.operational_components}/{summary.total_components}
                </p>
              </div>
              <div className="w-10 h-10 bg-unlimit-lime/20 rounded-xl flex items-center justify-center">
                <Activity className="h-5 w-5 text-navy-900" />
              </div>
            </div>
            <p className="text-[11px] text-gray-400 mt-2">
              Currently operational
            </p>
          </div>
        </div>
      )}

      {/* Component Statistics Table */}
      <div className="bg-white rounded-2xl shadow-card border border-gray-100 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <h2 className="text-base font-bold text-navy-900">Component Availability</h2>
          <p className="text-xs text-gray-400 mt-1">
            Detailed availability metrics for each component
          </p>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full">
            <thead>
              <tr className="border-b border-gray-100">
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  Component
                </th>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  Availability
                </th>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  Uptime
                </th>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  Incidents
                </th>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  Maintenances
                </th>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  Last Incident
                </th>
                <th className="px-6 py-3 text-left text-[11px] font-semibold text-gray-400 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {stats.map((stat) => (
                <tr key={stat.component_id} className="hover:bg-gray-50/50 transition-colors">
                  <td className="px-6 py-3.5 whitespace-nowrap">
                    <div className="text-sm font-semibold text-navy-900">
                      {stat.component_name}
                    </div>
                  </td>
                  <td className="px-6 py-3.5 whitespace-nowrap">
                    <span className={`px-2 py-1 text-[11px] font-semibold rounded-md ${getStatusColor(stat.component_status)}`}>
                      {stat.component_status.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-3.5 whitespace-nowrap">
                    <div className={`text-sm font-bold tabular-nums ${getAvailabilityColor(stat.availability_percentage)}`}>
                      {toNumber(stat.availability_percentage).toFixed(2)}%
                    </div>
                  </td>
                  <td className="px-6 py-3.5 whitespace-nowrap">
                    <div className="text-sm text-gray-600 tabular-nums">
                      {toNumber(stat.uptime_percentage).toFixed(2)}%
                    </div>
                  </td>
                  <td className="px-6 py-3.5 whitespace-nowrap">
                    <div className="text-sm text-gray-600 tabular-nums">
                      {stat.total_incidents || 0}
                    </div>
                  </td>
                  <td className="px-6 py-3.5 whitespace-nowrap">
                    <div className="text-sm text-gray-600 tabular-nums">
                      {stat.total_maintenances || 0}
                    </div>
                  </td>
                  <td className="px-6 py-3.5 whitespace-nowrap">
                    <div className="text-xs text-gray-400">
                      {formatDate(stat.last_incident_date)}
                    </div>
                  </td>
                  <td className="px-6 py-3.5 whitespace-nowrap">
                    <button
                      onClick={() => setSelectedComponent(
                        selectedComponent === stat.component_id ? null : stat.component_id
                      )}
                      className={`text-xs font-semibold px-3 py-1.5 rounded-md transition-colors ${
                        selectedComponent === stat.component_id
                          ? 'bg-navy-900 text-white'
                          : 'text-navy-900 bg-unlimit-gray-light hover:bg-gray-200'
                      }`}
                    >
                      {selectedComponent === stat.component_id ? 'Hide' : 'View'} Chart
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Daily Availability Chart */}
      {selectedComponent && dailyData.length > 0 && (
        <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-6 animate-slide-down">
          <h3 className="text-sm font-bold text-navy-900 mb-4">
            Daily Availability &mdash; {stats.find(s => s.component_id === selectedComponent)?.component_name}
          </h3>

          <div className="space-y-1">
            {dailyData.map((day) => (
              <div key={day.date} className="flex items-center space-x-2.5">
                <div className="text-[11px] text-gray-400 w-20 tabular-nums">
                  {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                </div>
                <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden">
                  <div
                    className={`h-full flex items-center justify-center text-[10px] font-semibold text-white transition-all duration-300 ${
                      day.availability_percentage >= 99.9 ? 'bg-emerald-500' :
                      day.availability_percentage >= 99.0 ? 'bg-yellow-500' :
                      day.availability_percentage >= 95.0 ? 'bg-orange-500' :
                      'bg-rose-300'
                    }`}
                    style={{ width: `${day.availability_percentage}%` }}
                  >
                    {toNumber(day.availability_percentage) >= 50 && `${toNumber(day.availability_percentage).toFixed(1)}%`}
                  </div>
                </div>
                <div className="text-[11px] text-gray-500 w-14 text-right tabular-nums">
                  {toNumber(day.availability_percentage).toFixed(2)}%
                </div>
                {day.incidents_count > 0 && (
                  <span className="text-[11px] text-rose-300 font-semibold">
                    {day.incidents_count} inc.
                  </span>
                )}
                {day.maintenances_count > 0 && (
                  <span className="text-[11px] text-blue-600 font-semibold">
                    {day.maintenances_count} mnt.
                  </span>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Legend */}
      <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-6">
        <h3 className="text-xs font-bold text-navy-900 uppercase tracking-wider mb-3">Availability Tiers</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-emerald-500 rounded-sm"></div>
            <span className="text-xs text-gray-500">&ge; 99.9% (Excellent)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-yellow-500 rounded-sm"></div>
            <span className="text-xs text-gray-500">99.0&ndash;99.9% (Good)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-orange-500 rounded-sm"></div>
            <span className="text-xs text-gray-500">95.0&ndash;99.0% (Fair)</span>
          </div>
          <div className="flex items-center space-x-2">
            <div className="w-3 h-3 bg-rose-300 rounded-sm"></div>
            <span className="text-xs text-gray-500">&lt; 95.0% (Poor)</span>
          </div>
        </div>
        <p className="text-[11px] text-gray-400 mt-4">
          <strong className="text-gray-500">Availability</strong> includes all operational time. <strong className="text-gray-500">Uptime</strong> excludes scheduled maintenance.
        </p>
      </div>
    </div>
  );
}
